import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Send, Bot } from "lucide-react";

const SUGGESTIONS = [
  "How do I write a strong marriage profile?",
  "What should I look for in a spouse?",
  "How do I involve my wali respectfully?",
  "Good questions to ask before nikah?",
];

export default function AICoach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/coach", { message: msg });
      const reply = data.reply || data.message || data.text || "…";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(err.response?.data?.detail) }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col" style={{ minHeight: "calc(100vh - 8rem)" }}>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-800 text-amber-300"><Bot className="h-6 w-6" /></div>
        <div>
          <h1 className="font-serif text-3xl font-bold text-emerald-950">AI Coach</h1>
          <p className="text-sm text-slate-500">Guidance and better-match tips, the halal way</p>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-emerald-900/10 bg-white p-5" data-testid="coach-messages">
        {messages.length === 0 ? (
          <div className="py-8 text-center">
            <Sparkles className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <h3 className="font-serif text-xl font-semibold text-emerald-900">Assalamu alaikum 👋</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">Ask me anything about finding a spouse, profiles, or preparing for nikah.</p>
            <div className="mx-auto mt-5 grid max-w-md gap-2 sm:grid-cols-2">
              {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} data-testid="coach-suggestion" className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-left text-sm text-emerald-900 hover:bg-emerald-50">{s}</button>)}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`coach-msg-${m.role}`}>
              <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-emerald-800 text-white" : "bg-emerald-50 text-slate-800"}`}>{m.content}</div>
            </div>
          ))
        )}
        {loading && <div className="flex justify-start"><div className="rounded-2xl bg-emerald-50 px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-emerald-700" /></div></div>}
        <div ref={endRef} />
      </div>

      <div className="mt-3 flex items-end gap-2">
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask the AI Coach…" rows={1} data-testid="coach-input" className="min-h-[48px] flex-1 resize-none rounded-2xl" />
        <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="coach-send-btn" className="h-12 rounded-2xl bg-emerald-800 px-5 text-white hover:bg-emerald-900"><Send className="h-4 w-4" /></Button>
      </div>
      <p className="mt-2 text-center text-xs text-slate-400">AI guidance for support only — consult your wali and a scholar for religious rulings.</p>
    </div>
  );
}
